// Browser side of analytics. Everything goes through here, so that a page works the same with no project set up, and
// so that what leaves the browser is decided in one place. See docs/analytics.md.

// The build that never loads a script from elsewhere: the page's Content-Security-Policy would refuse one anyway.
import posthog from 'posthog-js/no-external';
import { ANALYTICS_RELAY, AnalyticsConfig } from '../shared/analytics';

type Props = Record<string, unknown>;

/** A room code gets you into a private room, and `as` and `updated` say nothing useful. */
const PRIVATE_PARAMS = ['room', 'as', 'updated'];
const MAX_PENDING = 100;
/** The frame loop can throw the same error 60 times a second. */
const MAX_REPORTED_ERRORS = 10;

let ready = false;
let off = false;
let pending: (() => void)[] = [];
const reported = new Set<string>();

const PARAM_IN_TEXT = new RegExp(`([?&](?:${PRIVATE_PARAMS.join('|')})=)[^&#\\s"']*`, 'g');
/** A room's code as a message or an address gives it, such as "still in room 9N2TTK". */
const CODE_IN_TEXT = /\b(room[ /])[A-Z0-9]{6}\b/g;
const MAX_SCRUB_DEPTH = 8;

function scrubText(value: string): string {
  let out = value;
  if (/^https?:\/\//.test(out) && PRIVATE_PARAMS.some((p) => out.includes(`${p}=`))) {
    try {
      const u = new URL(out);
      for (const p of PRIVATE_PARAMS) u.searchParams.delete(p);
      out = u.toString();
    } catch {
      // The patterns below still apply.
    }
  }
  return out.replace(PARAM_IN_TEXT, '$1').replace(CODE_IN_TEXT, '$1[code]');
}

/** Everywhere, because the SDK also nests addresses, such as in an exception's stack. */
function scrubProps(props: unknown, depth = 0): void {
  if (!props || typeof props !== 'object' || depth > MAX_SCRUB_DEPTH) return;
  const holder = props as Props;
  for (const [k, v] of Object.entries(holder)) {
    if (typeof v === 'string') holder[k] = scrubText(v);
    else scrubProps(v, depth + 1);
  }
}

/** The SDK adds the page's address to events in several properties, including what it remembers about the first visit. */
export function scrubEvent<T extends { properties?: Props; $set?: Props; $set_once?: Props }>(event: T | null): T | null {
  if (!event) return event;
  scrubProps(event.properties);
  scrubProps(event.$set);
  scrubProps(event.$set_once);
  return event;
}

/** Runs now if the SDK is up, or once it is. Trouble in analytics stays in analytics. */
function run(fn: () => void): void {
  if (off) return;
  if (!ready) {
    if (pending.length < MAX_PENDING) pending.push(fn);
    return;
  }
  try {
    fn();
  } catch (err) {
    console.warn('analytics', err);
  }
}

export const analytics = {
  /** The config comes from the server, because one build of the client serves every environment. */
  init(config: AnalyticsConfig | null | undefined): void {
    if (!config) {
      off = true;
      pending = [];
      if (['localhost', '127.0.0.1'].includes(location.hostname)) console.warn('POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once POSTHOG_KEY is configured');
      return;
    }
    posthog.init(config.key, {
      api_host: ANALYTICS_RELAY,
      person_profiles: 'identified_only',
      disable_external_dependency_loading: true,
      // Not a cookie, which browsers would send to this site with every request.
      persistence: 'localStorage',
      // The game draws to a canvas, so autocapture and replay would see only the sign-in panel: its email and codes.
      autocapture: false,
      disable_session_recording: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      rageclick: false,
      disable_surveys: true,
      advanced_disable_flags: true,
      capture_pageview: true,
      capture_pageleave: true,
      // This build can't do these two. client/main.ts reports uncaught errors itself.
      capture_exceptions: false,
      capture_performance: false,
      before_send: (event) => scrubEvent(event),
    });
    posthog.register({ environment: config.env });
    ready = true;
    const queued = pending;
    pending = [];
    for (const fn of queued) run(fn);
  },

  /** The server couldn't say, so nothing queued can ever go. */
  disable(): void {
    off = true;
    pending = [];
  },

  track(event: string, props: Props = {}): void {
    const timestamp = new Date();
    run(() => posthog.capture(event, props, { timestamp }));
  },

  /** For the moment a page is going away, when only a beacon gets out. */
  trackOnExit(event: string, props: Props = {}): void {
    run(() => posthog.capture(event, props, { transport: 'sendBeacon' }));
  },

  /** Ties this browser to an account. The account's opaque id only: never the address. */
  identify(accountId: string, props: Props = {}): void {
    run(() => {
      // Someone else's account on this browser must not be merged with this one.
      if (posthog.get_property('$user_state') === 'identified' && posthog.get_distinct_id() !== accountId) posthog.reset(true);
      posthog.identify(accountId, props);
    });
  },

  /**
   * After a session ends, so that whoever uses this browser next starts as someone new, and so that nothing more is
   * recorded against a deleted account. Only if someone is signed in: a new visitor's id links their visit to their sign-up.
   */
  reset(): void {
    run(() => {
      if (posthog.get_property('$user_state') === 'identified') posthog.reset(true);
    });
  },

  exception(err: unknown, props: Props = {}): void {
    const key = err instanceof Error ? err.message : String(err);
    if (reported.has(key) || reported.size >= MAX_REPORTED_ERRORS) return;
    reported.add(key);
    run(() => posthog.captureException(err, props));
  },

  /** So that the room's events land in this browser's session. */
  sessionId(): string | undefined {
    if (!ready) return undefined;
    try {
      return posthog.get_session_id();
    } catch {
      return undefined;
    }
  },
};
